import { useState } from "react";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";

interface PackagesState {
  error: string;
  loading: boolean;
  membershipPackages: MembershipPackage[];
  getMembershipPackages: (onSuccess?: (response: MembershipPackage[]) => void) => void;
  getMembershipPackage: (
    packageId: string,
    onSuccess?: (response: MembershipPackage) => void
  ) => void;
  updateMembershipPackage: (
    packageData: MembershipPackage,
    onSuccess?: (response: MembershipPackage) => void
  ) => void;
  addMembershipPackage: (
    packageData: MembershipPackage,
    onSuccess?: (newPackage: MembershipPackage) => void
  ) => void;
  deleteMembershipPackage: (
    packageId: string,
    onSuccess?: () => void
  ) => void;
  markBuyMembershipPackageAsPaid: (
    patientId: string,
    transactionId: string,
    onSuccess?: () => void
  ) => void;
  getPatientCredits: (
    patientId: string,
    onSuccess?: (response: PatientCreditsResponse) => void
  ) => void;
  getBuyMembershipPackageList: (
    patientId: string,
    onSuccess?: (response: BuyMembershipPackageList[]) => void
  ) => void;
  changePatientMembershipPackageStatus: (
		patientId: string,
		patientMembershipPackageId: string,
		status: string,
		onSuccess?: () => void
	) => void;
  resetError: VoidFunction;
}

const useMembershipPackages = (): PackagesState => {
  const [membershipPackages, setMembershipPackages] = useState<MembershipPackage[]>([]);
  const [error, setError] = useState<string>("");

  const {
    getData: getMembershipPackagesRequest,
    loading: getMembershipPackagesLoading,
  } = useApi<MembershipPackage[]>();

  const {
    getData: getMembershipPackageRequest,
    loading: getMembershipPackageLoading,
  } = useApi<MembershipPackage>();

  const {
    postData: addMembershipPackageRequest,
    loading: addMembershipPackageLoading,
  } = useApi<MembershipPackage>();

  const {
    putData: updateMembershipPackageRequest,
    loading: updateMembershipPackageLoading,
  } = useApi<MembershipPackage>();

  const {
    deleteData: deleteMembershipPackageRequest,
    loading: deleteMembershipPackageLoading,
  } = useApi();

  const {
    getData: markBuyMembershipPackageAsPaidRequest,
    loading: markBuyMembershipPackageAsPaidLoading,
  } = useApi();

  const {
    getData: getPatientCreditsRequest,
    loading: getPatientCreditsRequestLoading,
  } = useApi();

  const {
    getData: getBuyMembershipPackageListRequest,
    loading: getBuyMembershipPackageListRequestLoading,
  } = useApi();

  const {
    postData: updatePatientMembershipPackageStatusRequest,
    loading: updatePatientMembershipPackageStatusLoading,
  } = useApi();

  const getMembershipPackages = async (onSuccess?: (response: MembershipPackage[]) => void) => {
    setError("");
    await getMembershipPackagesRequest(
      API_ROUTES.MEMBERSHIPPACKAGES.base,
      (response: MembershipPackage[]) => {
        setMembershipPackages(response);
        onSuccess?.(response);
      },
      setError
    );
  };

  const getMembershipPackage = async (
    MembershipPackageId: string,
    onSuccess?: (response: MembershipPackage) => void
  ) => {
    setError("");
    await getMembershipPackageRequest(
      API_ROUTES.MEMBERSHIPPACKAGES.byId(MembershipPackageId),
      (response: MembershipPackage) => {
        onSuccess?.(response);
      },
      setError
    );
  };

  const addMembershipPackage = async (
    MembershipPackageData: MembershipPackage,
    onSuccess?: (newMembershipPackage: MembershipPackage) => void
  ) => {
    setError("");
    await addMembershipPackageRequest(
      API_ROUTES.MEMBERSHIPPACKAGES.base,
      MembershipPackageData,
      (response: MembershipPackage) => {
        onSuccess?.(response);
        // Refresh the packages list
        getMembershipPackages();
      },
      setError
    );
  };

  const updateMembershipPackage = async (
    MembershipPackageData: MembershipPackage,
    onSuccess?: (response: MembershipPackage) => void
  ) => {
    setError("");
    // Omit non-updatable fields if present in the object (e.g., createdAt, updatedAt, index)
    const { createdAt, updatedAt, index, ...payload } = (MembershipPackageData as unknown) as Record<string, any>;
    await updateMembershipPackageRequest(
      API_ROUTES.MEMBERSHIPPACKAGES.byId(MembershipPackageData.id),
      payload as MembershipPackage,
      (response: MembershipPackage) => {
        onSuccess?.(response);
        // Refresh the MembershipPackages list
        getMembershipPackages();
      },
      setError
    );
  };

  const deleteMembershipPackage = async (
    MembershipPackageId: string,
    onSuccess?: () => void
  ) => {
    setError("");
    await deleteMembershipPackageRequest(
      API_ROUTES.MEMBERSHIPPACKAGES.byId(MembershipPackageId),
      {},
      () => {
        onSuccess?.();
        // Refresh the MembershipPackages list
        getMembershipPackages();
      },
      setError
    );
  };

  const markBuyMembershipPackageAsPaid = async (
    patientId: string,
    transactionId: string,
    onSuccess?: () => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PATIENT.patientBuyMembershipPackageSuccess
      .replace("{{PATIENT_ID}}", patientId)
      .replace("{{TRANSACTION_ID}}", transactionId);
    await markBuyMembershipPackageAsPaidRequest(
      endPoint,
      () => {
        onSuccess?.();
        // Refresh the MembershipPackages list
        getMembershipPackages();
      },
      setError
    );
  };

  const getPatientCredits = async (
    patientId: string,
    onSuccess?: (response: PatientCreditsResponse) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PATIENT.getPatientCredits
      .replace("{{PATIENT_ID}}", patientId)
    await getPatientCreditsRequest(
      endPoint,
      (response: PatientCreditsResponse) => {
        onSuccess?.(response);
      },
      setError
    );
  };

  const getBuyMembershipPackageList = async (
    patientId: string,
    onSuccess?: (response: BuyMembershipPackageList[]) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.PATIENT.getPatientMembershipPackageList
      .replace("{{PATIENT_ID}}", patientId)
    await getBuyMembershipPackageListRequest(
      endPoint,
      (response: BuyMembershipPackageList[]) => {
        onSuccess?.(response);
      },
      setError
    );
  };

  const changePatientMembershipPackageStatus = async (
		patientId: string,
		patientMembershipPackageId: string,
		status: string,
		onSuccess?: (response: BuyMembershipPackageList[]) => void
	) => {
		const endPoint = API_ROUTES.MEMBERSHIPPACKAGES.updatePatientsessionbookingStatus;
		await updatePatientMembershipPackageStatusRequest(endPoint, {patientId, patientMembershipPackageId, status}, onSuccess, setError);
	};

  const resetError = () => {
    setError("");
  };

  return {
    error,
    loading:
      getMembershipPackagesLoading ||
      getMembershipPackageLoading ||
      addMembershipPackageLoading ||
      updateMembershipPackageLoading ||
      deleteMembershipPackageLoading ||
      markBuyMembershipPackageAsPaidLoading ||
      getPatientCreditsRequestLoading || 
      getBuyMembershipPackageListRequestLoading ||
      updatePatientMembershipPackageStatusLoading,
    membershipPackages,
    getMembershipPackages,
    getMembershipPackage,
    updateMembershipPackage,
    addMembershipPackage,
    deleteMembershipPackage,
    resetError,
    markBuyMembershipPackageAsPaid,
    getPatientCredits,
    getBuyMembershipPackageList,
    changePatientMembershipPackageStatus,
  };
};

export default useMembershipPackages;